import type { LoadDescriptor } from "building-types/electrical/electricalTypes";
import {
  ControllerSelectionType,
  ControllerSwitchType,
  ControllerType,
  ElectricalBoxType,
  LoadSymbol,
  LoadType,
} from "building-types/electrical/electricalTypesEnum";

export function loadDescriptorNew({
  id,
  name,
  planLabel,
  symbol,
  boxType,
  erpCode,
}: Omit<LoadDescriptor, "catalogItemType">): LoadDescriptor {
  return {
    id,
    catalogItemType: "LOAD_DESCRIPTOR",
    name,
    planLabel,
    symbol,
    boxType,
    erpCode,
  };
}

export const defaultLoadCatalog: {
  [key in LoadType]: LoadDescriptor;
} = {
  MOUNTED_CEILING_LIGHT: loadDescriptorNew({
    id: LoadType.MOUNTED_CEILING_LIGHT,
    name: "Ceiling Mounted Light",
    symbol: LoadSymbol.CEILING_LIGHT,
    boxType: ElectricalBoxType.ROUND,
  }),
  RECESSED_CEILING_LIGHT: loadDescriptorNew({
    id: LoadType.RECESSED_CEILING_LIGHT,
    name: "Recessed Ceiling Light",
    symbol: LoadSymbol.CIRCLE_DL_60_40,
    boxType: ElectricalBoxType.NONE,
  }),
  RECESSED_CEILING_LIGHT_MINI: loadDescriptorNew({
    id: LoadType.RECESSED_CEILING_LIGHT_MINI,
    name: "Recessed Ceiling Light Mini",
    symbol: LoadSymbol.CIRCLE_4,
    planLabel: "MINI",
    boxType: ElectricalBoxType.NONE,
  }),
  PENDANT_CEILING_LIGHT: loadDescriptorNew({
    id: LoadType.PENDANT_CEILING_LIGHT,
    name: "Pendant Light",
    symbol: LoadSymbol.CIRCLE_DL_60_40,
    planLabel: "P",
    boxType: ElectricalBoxType.ROUND_PAN,
  }),
  KEYLESS_CEILING_LIGHT: loadDescriptorNew({
    id: LoadType.KEYLESS_CEILING_LIGHT,
    name: "Keyless Ceiling Light",
    symbol: LoadSymbol.CEILING_LIGHT,
    planLabel: "K",
    boxType: ElectricalBoxType.ROUND_PAN,
  }),
  MOUNTED_CEILING_LED_LIGHT: loadDescriptorNew({
    id: LoadType.MOUNTED_CEILING_LED_LIGHT,
    name: "LED Light",
    symbol: LoadSymbol.CIRCLE_DL_60_40,
    planLabel: "LED",
    boxType: ElectricalBoxType.ROUND_PAN,
  }),
  FLOURESCENT_CEILING_LIGHT: loadDescriptorNew({
    id: LoadType.FLOURESCENT_CEILING_LIGHT,
    name: "Flourescent Light 2x4",
    symbol: LoadSymbol.RECTANGLE_DL_2X4,
    boxType: ElectricalBoxType.NONE,
  }),
  FLOURESCENT_CEILING_LIGHT_1X4: loadDescriptorNew({
    id: LoadType.FLOURESCENT_CEILING_LIGHT_1X4,
    name: "Flourescent Light 1x4",
    symbol: LoadSymbol.RECTANGLE_DL_1X4,
    boxType: ElectricalBoxType.NONE,
  }),
  UNDER_COUNTER_LIGHT: loadDescriptorNew({
    id: LoadType.UNDER_COUNTER_LIGHT,
    name: "Under-Counter Light",
    symbol: LoadSymbol.CIRCLE_DL_60_40,
    planLabel: "UC",
    boxType: ElectricalBoxType.NONE,
  }),
  TRACK_CEILING_LIGHT: loadDescriptorNew({
    id: LoadType.TRACK_CEILING_LIGHT,
    name: "Track Light",
    symbol: LoadSymbol.TRACK_CEILING_LIGHT,
    boxType: ElectricalBoxType.NONE,
  }),
  MOUNTED_WALL_LIGHT: loadDescriptorNew({
    id: LoadType.MOUNTED_WALL_LIGHT,
    name: "Wall Mounted Light",
    symbol: LoadSymbol.MOUNTED_WALL_LIGHT,
    boxType: ElectricalBoxType.ROUND_PAN,
  }),
  FLOOD_LIGHT: loadDescriptorNew({
    id: LoadType.FLOOD_LIGHT,
    name: "Flood Light",
    symbol: LoadSymbol.FLOOD_LIGHT,
    boxType: ElectricalBoxType.ROUND,
  }),
  OUTLET: loadDescriptorNew({
    id: LoadType.OUTLET,
    name: "Outlet",
    symbol: LoadSymbol.OUTLET,
    boxType: ElectricalBoxType.ONE_GANG,
  }),
  OUTLET_240V: loadDescriptorNew({
    id: LoadType.OUTLET_240V,
    name: "Outlet 240V",
    symbol: LoadSymbol.HIGH_VOLTAGE_OUTLET,
    planLabel: "240V",
    boxType: ElectricalBoxType.ONE_GANG,
  }),
  OUTLET_220V: loadDescriptorNew({
    id: LoadType.OUTLET_220V,
    name: "Outlet 220V",
    symbol: LoadSymbol.HIGH_VOLTAGE_OUTLET,
    planLabel: "220V",
    boxType: ElectricalBoxType.ONE_GANG,
  }),
  OUTLET_120_240V: loadDescriptorNew({
    id: LoadType.OUTLET_120_240V,
    name: "Outlet 120/240V",
    symbol: LoadSymbol.OUTLET_SPECIAL,
    planLabel: "120/240V",
    boxType: ElectricalBoxType.TWO_GANG,
  }),
  OUTLET_GFCI: loadDescriptorNew({
    id: LoadType.OUTLET_GFCI,
    name: "GFCI Outlet",
    symbol: LoadSymbol.OUTLET,
    planLabel: "GFCI",
    boxType: ElectricalBoxType.ONE_GANG,
  }),
  OUTLET_GFCI_WP: loadDescriptorNew({
    id: LoadType.OUTLET_GFCI_WP,
    name: "GFCI Outlet Waterproof",
    symbol: LoadSymbol.OUTLET,
    planLabel: "GFCI (WP)",
    boxType: ElectricalBoxType.ONE_GANG_WP,
  }),
  OUTLET_FLOOR: loadDescriptorNew({
    id: LoadType.OUTLET_FLOOR,
    name: "Outlet in Floor",
    symbol: LoadSymbol.OUTLET,
    planLabel: "F",
    boxType: ElectricalBoxType.FLOOR_SINGLE,
  }),
  OUTLET_HDMI: loadDescriptorNew({
    id: LoadType.OUTLET_HDMI,
    name: "HDMI",
    symbol: LoadSymbol.OUTLET,
    planLabel: "HDMI",
    boxType: ElectricalBoxType.ONE_GANG,
  }),
  OUTLET_CAT5: loadDescriptorNew({
    id: LoadType.OUTLET_CAT5,
    name: "CAT5 Outlet",
    symbol: LoadSymbol.OUTLET,
    planLabel: "CAT5",
    boxType: ElectricalBoxType.ONE_GANG,
  }),
  CABLE_OUTLET: loadDescriptorNew({
    id: LoadType.CABLE_OUTLET,
    name: "Cable Outlet",
    symbol: LoadSymbol.OUTLET,
    planLabel: "C",
    boxType: ElectricalBoxType.ONE_GANG,
  }),
  TELEVISION: loadDescriptorNew({
    id: LoadType.TELEVISION,
    name: "TV",
    symbol: LoadSymbol.COMMS_TV,
    planLabel: "TV",
    boxType: ElectricalBoxType.ONE_GANG,
  }),
  TELEPHONE: loadDescriptorNew({
    id: LoadType.TELEPHONE,
    name: "Phone",
    symbol: LoadSymbol.COMMS_TV,
    planLabel: "PH",
    boxType: ElectricalBoxType.ONE_GANG,
  }),
  SMOKE_DETECTOR: loadDescriptorNew({
    id: LoadType.SMOKE_DETECTOR,
    name: "Smoke Detector",
    symbol: LoadSymbol.CIRCLE_DL_45_15,
    planLabel: "SD",
    boxType: ElectricalBoxType.ROUND_PAN,
  }),
  CARBON_MONOXIDE_DETECTOR: loadDescriptorNew({
    id: LoadType.CARBON_MONOXIDE_DETECTOR,
    name: "Carbon Monoxide/Smoke Detector",
    symbol: LoadSymbol.CIRCLE_DL_45_15,
    planLabel: "S/CO",
    boxType: ElectricalBoxType.ROUND_PAN,
  }),
  THERMOSTAT: loadDescriptorNew({
    id: LoadType.THERMOSTAT,
    name: "Thermostat",
    symbol: LoadSymbol.CIRCLE_7,
    planLabel: "T",
    boxType: ElectricalBoxType.NONE,
  }),
  BATH_FAN: loadDescriptorNew({
    id: LoadType.BATH_FAN,
    name: "Bath Fan",
    symbol: LoadSymbol.BATH_FAN,
    boxType: ElectricalBoxType.NONE,
  }),
  BATH_FAN_LIGHT: loadDescriptorNew({
    id: LoadType.BATH_FAN_LIGHT,
    name: "Bath Fan Light",
    symbol: LoadSymbol.BATH_FAN_LIGHT,
    boxType: ElectricalBoxType.NONE,
  }),
  DISH_DISPOSAL: loadDescriptorNew({
    id: LoadType.DISH_DISPOSAL,
    name: "Disposal",
    symbol: LoadSymbol.CIRCLE_7,
    planLabel: "D",
    boxType: ElectricalBoxType.NONE,
  }),
  DOORBELL_CHIME: loadDescriptorNew({
    id: LoadType.DOORBELL_CHIME,
    name: "Doorbell Chime",
    symbol: LoadSymbol.CIRCLE_8,
    planLabel: "DC",
    boxType: ElectricalBoxType.NONE,
  }),
  GARAGE_DOOR_OPENER: loadDescriptorNew({
    id: LoadType.GARAGE_DOOR_OPENER,
    name: "Garage Door Opener",
    symbol: LoadSymbol.CIRCLE_7,
    planLabel: "G",
    boxType: ElectricalBoxType.ONE_GANG,
  }),
  CEILING_FAN: loadDescriptorNew({
    id: LoadType.CEILING_FAN,
    name: "Ceiling Fan",
    symbol: LoadSymbol.CEILING_FAN,
    boxType: ElectricalBoxType.FAN_RATED,
  }),
  CEILING_FAN_LIGHT: loadDescriptorNew({
    id: LoadType.CEILING_FAN_LIGHT,
    name: "Ceiling Fan Light",
    symbol: LoadSymbol.CEILING_FAN_LIGHT,
    boxType: ElectricalBoxType.FAN_RATED,
  }),
  VIRTUAL_CEILING_FAN_LIGHT: loadDescriptorNew({
    id: LoadType.VIRTUAL_CEILING_FAN_LIGHT,
    name: "",
    symbol: LoadSymbol.NONE,
  }),
  FAN_PREWIRE: loadDescriptorNew({
    id: LoadType.FAN_PREWIRE,
    name: "Fan Prewire",
    symbol: LoadSymbol.FAN_PREWIRE,
    boxType: ElectricalBoxType.FAN_RATED,
  }),
  HOSE_BIB: loadDescriptorNew({
    id: LoadType.HOSE_BIB,
    name: "Hose Bib",
    symbol: LoadSymbol.CIRCLE_7,
    planLabel: "HB",
  }),
  FLOOR_TO_FLOOR_TRANSITION: loadDescriptorNew({
    id: LoadType.FLOOR_TO_FLOOR_TRANSITION,
    name: "Floor to Floor Transition",
    symbol: LoadSymbol.FLOOR_TO_FLOOR_TRANSITION,
  }),
};

export function LoadSymbolNamesForLegend(loadDescriptor: LoadDescriptor) {
  let loadName = loadDescriptor.name;
  if (loadDescriptor.id === LoadType.FLOOR_TO_FLOOR_TRANSITION) {
    loadName = " ";
  }

  return loadName;
}

export const ControllerSelectionTypesForUi: { [key in ControllerSelectionType]?: true } = {
  [ControllerSelectionType.PRIMARY_ENTRANCE]: true,
  [ControllerSelectionType.PRIMARY_ENTRANCE_OUTSIDE_ROOMSET]: true,
  [ControllerSelectionType.NEAREST_CANDIDATE]: true,
  [ControllerSelectionType.NEAREST_CANDIDATE_OUTSIDE_ROOMSET]: true,
  [ControllerSelectionType.NEAREST_DOOR_OUTSIDE_ROOMSET]: true,
  [ControllerSelectionType.DOOR_BY_ROOM_TYPE]: true,
  [ControllerSelectionType.DOOR_BY_ROOM_TYPE_OUTSIDE_ROOMSET]: true,
  [ControllerSelectionType.BY_CONTROLLER_TAG]: true,
  [ControllerSelectionType.DOOR_BY_ROOM_IDS]: true,
  [ControllerSelectionType.NEAREST_EXPLICIT_WALL]: true,
  // [ControllerSelectionType.EXPLICIT_POINT]:true, // COVERED BY NEAREST_EXPLICIT_WALL
  // [ControllerSelectionType.NONE]:true,
};

export const ControllerSelectionTypesForUiWithoutRoomHash: { [key in ControllerSelectionType]?: true } = {
  [ControllerSelectionType.PRIMARY_ENTRANCE]: true,
  [ControllerSelectionType.PRIMARY_ENTRANCE_OUTSIDE_ROOMSET]: true,
  [ControllerSelectionType.NEAREST_CANDIDATE]: true,
  [ControllerSelectionType.NEAREST_CANDIDATE_OUTSIDE_ROOMSET]: true,
  [ControllerSelectionType.NEAREST_DOOR_OUTSIDE_ROOMSET]: true,
  [ControllerSelectionType.DOOR_BY_ROOM_TYPE]: true,
  [ControllerSelectionType.DOOR_BY_ROOM_TYPE_OUTSIDE_ROOMSET]: true,
  [ControllerSelectionType.BY_CONTROLLER_TAG]: true,
  [ControllerSelectionType.DOOR_BY_ROOM_IDS]: true,
};

export function isControllerType(type: string): type is ControllerType {
  return Object.values(ControllerType).includes(type as ControllerType);
}

export function isControllerSwitchType(switchType: string): switchType is ControllerSwitchType {
  return Object.values(ControllerSwitchType).includes(switchType as ControllerSwitchType);
}
